import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

// Fade + rise on scroll into view
export const Reveal = ({ children, delay = 0, y = 40, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    {...rest}
  >
    {children}
  </motion.div>
);

// Fade + rise on page load. Pure CSS so it can never get stuck hidden.
// `delay` is in seconds.
export const FadeUp = ({ children, delay = 0, className = "", as: Tag = "div", ...rest }) => (
  <Tag className={`anim-fade-up ${className}`} style={{ animationDelay: `${delay}s` }} {...rest}>
    {children}
  </Tag>
);

// Masked line-by-line reveal for large display headings, played on page load.
// Pure CSS animation: the text is always visible once the animation finishes,
// even if JavaScript-driven animation is unavailable.
export const MaskedLines = ({ lines = [], className = "", lineClassName = "", start = 0.15 }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span key={i} className="reveal-mask">
        <span
          className={`block anim-rise ${lineClassName}`}
          style={{ animationDelay: `${start + i * 0.12}s` }}
        >
          {line}
        </span>
      </span>
    )).flatMap((el, i) => [el, " "])}
  </span>
);

// Masked reveal that triggers on scroll (for section headings).
// Uses IntersectionObserver + CSS so the text can never be left hidden.
export const MaskedLinesInView = ({ lines = [], className = "", lineClassName = "" }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return;
    if (typeof IntersectionObserver === "undefined") { setShown(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { setShown(true); io.disconnect(); } },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.1 }
    );
    io.observe(el);
    // Safety net: if it's on screen but the observer never fires, show it anyway.
    const t = setTimeout(() => setShown(true), 2500);
    return () => { io.disconnect(); clearTimeout(t); };
  }, [shown]);

  return (
    <span ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className="reveal-mask">
          <span
            className={`block ${shown ? "anim-rise" : "translate-y-[110%]"} ${lineClassName}`}
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            {line}
          </span>
        </span>
      )).flatMap((el) => [el, " "])}
    </span>
  );
};
